/**
 * sprayPhotoCache — the page side of the service worker's Spraywall photo cache
 * (docs/spec/01f-tab-loading.md, "The Spraywall photo cache and warm-up").
 *
 * The worker (`public/sw.js`) keeps the wall photos in `sendly-spray-photos-v1`
 * and answers them cache-first. Two things are left to the page:
 *
 *  - REPORTING a photo that failed to load. The cache stores opaque responses
 *    whose status is hidden, so a 403 / 404 / 5xx from Storage looks exactly
 *    like a photo. An element that fails on its URL tells the worker to drop
 *    that entry (`reportSprayPhotoFailed`), so a bad entry fails at most once.
 *  - WARMING the cache in the background, so the first open of the tab on a
 *    device already finds the photos (`sprayWallPrefetcher`, one entry of the
 *    `useBackgroundPrefetch` prefetchers). The same run first reads each warmed
 *    wall's live version document (`prefetchSprayVersion`), so the problem
 *    thumbnails have their hold outlines at the first wall open too.
 *
 * Every function here is a convenience: none of them throws or rejects.
 */
import { parseSprayWallHash, readSprayLastLocation } from "./sprayWallTab";
import { prefetchSprayVersion } from "../hooks/useSprayVersion";

/** Photos warmed per run: a gym has a handful of walls, and each decode is ~17 MB briefly. */
export const SPRAY_PHOTO_WARM_MAX = 6;

/** One photo's decode is abandoned after this long (the next photo goes on). */
export const SPRAY_PHOTO_WARM_CAP_MS = 20000;

/** The worker controlling this page, or null (first visit, no service worker, a test). */
function controllingWorker() {
  try {
    return (typeof navigator !== "undefined" && navigator.serviceWorker && navigator.serviceWorker.controller) || null;
  } catch {
    return null;
  }
}

/**
 * Tell the worker this photo URL did not load, so it deletes the cached entry
 * and the next load goes to the network. No-op without a URL or a controlling
 * worker; never throws.
 */
export function reportSprayPhotoFailed(url) {
  if (typeof url !== "string" || url === "") return;
  const worker = controllingWorker();
  if (!worker) return;
  try { worker.postMessage({ type: "SPRAY_PHOTO_FAILED", url }); } catch { /* a report is a convenience */ }
}

/**
 * The walls the warm-up covers, in the order the tab will show them: the wall of
 * the remembered location first (when it is one of `walls`), then every wall in
 * the given (picker) order. Walls without a `photo.url` are skipped, a wall id
 * (or photo URL) appears once, at most {@link SPRAY_PHOTO_WARM_MAX}. One list
 * drives both the live version reads and the photos, so they cover the same walls.
 *
 * @param {Array<{ id: string, currentVersionId?: string, photo?: { url?: string } }>} walls the viewer's visible walls
 * @param {string|null} rememberedHash the gym's remembered Spraywall hash, or null
 */
export function sprayPhotoWarmWalls(walls, rememberedHash) {
  const list = Array.isArray(walls) ? walls : [];
  const rememberedId = rememberedHash ? parseSprayWallHash(rememberedHash).wallId : null;
  const remembered = rememberedId ? list.find((w) => w && w.id === rememberedId) : null;
  const ordered = remembered ? [remembered, ...list.filter((w) => w !== remembered)] : list;
  const picked = [];
  const ids = new Set();
  const urls = new Set();
  for (const wall of ordered) {
    const url = wall && wall.photo && wall.photo.url;
    if (typeof url !== "string" || url === "" || urls.has(url) || ids.has(wall.id)) continue;
    ids.add(wall.id);
    urls.add(url);
    picked.push(wall);
    if (picked.length >= SPRAY_PHOTO_WARM_MAX) break;
  }
  return picked;
}

/** The photo URLs of {@link sprayPhotoWarmWalls}, in the same order. */
export function sprayPhotoWarmUrls(walls, rememberedHash) {
  return sprayPhotoWarmWalls(walls, rememberedHash).map((wall) => wall.photo.url);
}

/** Resolves when the image is decoded, rejects when it cannot be; resolves "timeout" after the cap. */
function decodeWithCap(img) {
  let timer;
  const cap = new Promise((resolve) => { timer = setTimeout(() => resolve("timeout"), SPRAY_PHOTO_WARM_CAP_MS); });
  const decoded = typeof img.decode === "function"
    ? img.decode()
    : new Promise((resolve, reject) => { img.onload = () => resolve(); img.onerror = () => reject(new Error("load")); });
  return Promise.race([decoded, cap]).finally(() => clearTimeout(timer));
}

/**
 * Load the photos one after the other through the service worker, so its cache
 * fills. A `new Image()` without `crossOrigin` makes a `no-cors` request, the
 * kind the worker's photo branch answers. `decode()` proves the bytes are a
 * complete image. A rejection is reported only when `naturalWidth === 0` (the
 * bytes are not a usable image, so the cached entry is bad); a rejection on an
 * image that did load (`naturalWidth > 0`) is NOT — WebKit can reject
 * `decode()` on a large image outside the DOM, and deleting a good entry on
 * every warm-up would silently disable the cache. A timeout is NOT reported
 * either — a slow network is no proof of a bad entry. Without `decode()` the
 * `error` event stands in for the rejection. The `Image` is dropped after
 * each photo (a decoded 2400 × 1800 bitmap is ~17 MB).
 *
 * Without a controlling worker nothing would keep the bytes, so nothing runs.
 * Never rejects.
 */
export async function warmSprayPhotos(urls) {
  if (!controllingWorker() || !Array.isArray(urls)) return;
  for (const url of urls) {
    if (typeof url !== "string" || url === "") continue;
    try {
      let img = new Image();
      img.decoding = "async";
      img.src = url;
      try {
        await decodeWithCap(img);
      } catch {
        // A rejection on an image that did load says nothing about the entry.
        if (!(img.naturalWidth > 0)) reportSprayPhotoFailed(url);
      }
      img = null;
    } catch { /* a warm-up is a convenience */ }
  }
}

/**
 * The `spraywall` entry of the background prefetchers: `undefined` when the tab
 * is not in the viewer's bar or there is no visible wall, otherwise one async
 * run — the picker's counts first (2+ walls: a one-wall gym opens the wall page
 * and never shows the picker), then the live version documents of the warm
 * walls (the hold outlines the problem thumbnails draw; a few reads, cheap next
 * to ~1 MB photos, one after the other), then the photos (1+ walls).
 */
export function sprayWallPrefetcher({ inBar, walls, pickerSources, gymId }) {
  if (!inBar || !Array.isArray(walls) || walls.length === 0) return undefined;
  return async () => {
    if (walls.length >= 2 && pickerSources && typeof pickerSources.prefetch === "function") {
      try { await pickerSources.prefetch(walls); } catch { /* the photos still go on */ }
    }
    const warmWalls = sprayPhotoWarmWalls(walls, readSprayLastLocation(gymId));
    for (const wall of warmWalls) {
      if (!wall.currentVersionId) continue;
      try {
        await prefetchSprayVersion({ gymId, wallId: wall.id, versionId: wall.currentVersionId });
      } catch { /* the photos still go on */ }
    }
    await warmSprayPhotos(warmWalls.map((wall) => wall.photo.url));
  };
}
